import {
  Box, Typography, MenuItem, TextField, Chip, Stack,
} from '@mui/material';
import { TYPE } from '@/theme/typography';

export interface CountryOption {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
  enabled: boolean;
}

export const COUNTRIES: CountryOption[] = [
  { code: 'NG', name: 'Nigeria', dialCode: '+234', flag: '🇳🇬', enabled: true },
  { code: 'GH', name: 'Ghana', dialCode: '+233', flag: '🇬🇭', enabled: true },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦', enabled: true },
  { code: 'KE', name: 'Kenya', dialCode: '+254', flag: '🇰🇪', enabled: true },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧', enabled: true },
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸', enabled: true },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦', enabled: true },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', flag: '🇦🇪', enabled: true },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳', enabled: true },
  { code: 'EG', name: 'Egypt', dialCode: '+20', flag: '🇪🇬', enabled: true },
  { code: 'TZ', name: 'Tanzania', dialCode: '+255', flag: '🇹🇿', enabled: true },
  { code: 'UG', name: 'Uganda', dialCode: '+256', flag: '🇺🇬', enabled: true },
  { code: 'RW', name: 'Rwanda', dialCode: '+250', flag: '🇷🇼', enabled: true },
  { code: 'ET', name: 'Ethiopia', dialCode: '+251', flag: '🇪🇹', enabled: true },
  { code: 'CM', name: 'Cameroon', dialCode: '+237', flag: '🇨🇲', enabled: true },
  { code: 'CI', name: "Cote d'Ivoire", dialCode: '+225', flag: '🇨🇮', enabled: true },
  { code: 'SN', name: 'Senegal', dialCode: '+221', flag: '🇸🇳', enabled: true },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳', enabled: true },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪', enabled: true },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷', enabled: true },
].sort((a, b) => a.name.localeCompare(b.name));

const countryMap = Object.fromEntries(COUNTRIES.map((c) => [c.code, c]));

/** Look up a country option by its ISO code */
// eslint-disable-next-line react-refresh/only-export-components
export function getCountryByCode(code: string): CountryOption | undefined {
  return countryMap[code];
}

interface CountrySelectProps {
  value: string;
  onChange: (code: string) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  showDialCode?: boolean;
  disabled?: boolean;
}

export default function CountrySelect({
  value,
  onChange,
  label = 'Country',
  error,
  helperText,
  size = 'small',
  fullWidth = true,
  showDialCode = false,
  disabled = false,
}: CountrySelectProps) {
  const selected = countryMap[value];

  return (
    <TextField
      select
      label={label || undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={error}
      helperText={helperText}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      slotProps={{
        select: {
          renderValue: () =>
            selected ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography component="span" sx={{ fontSize: '1.2em', lineHeight: 1 }}>{selected.flag}</Typography>
                <Typography component="span" variant="body2">
                  {selected.name}
                  {showDialCode && ` (${selected.dialCode})`}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">Select country</Typography>
            ),
        },
      }}
    >
      {COUNTRIES.map((c) => (
        <MenuItem key={c.code} value={c.code} disabled={!c.enabled}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
            <Typography sx={{ fontSize: '1.3em', lineHeight: 1 }}>{c.flag}</Typography>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2">{c.name}</Typography>
            </Box>
            {showDialCode && (
              <Typography variant="body2" color="text.secondary">{c.dialCode}</Typography>
            )}
            {!c.enabled && (
              <Chip label="Coming soon" size="small" sx={{ height: 20, fontSize: TYPE.xs }} />
            )}
          </Box>
        </MenuItem>
      ))}
    </TextField>
  );
}
